import { motion, useScroll, useTransform, useMotionValueEvent } from "framer-motion";
import { useRef, memo, useMemo, useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import {
  EASING_CURVES,
  ANIMATION_TIMING,
} from "../../utils/motion";
import { useScrollContext, useSectionVisibility } from "../../utils/ScrollContext";

export const SAMText = memo(({ textes,  setIsBackgroundWhite}) => {
  const { ref: sectionRef, inView } = useSectionVisibility('sam-text', 0.3);

  const targetRef = useRef(null);
  const { isSectionVisible, getVisibleSections } = useScrollContext();

  const { scrollYProgress } = useScroll({
    target: targetRef,
    offset: ["start end", "end start"],
  });


  // Memoize scroll progress ranges
  const scrollRanges = useMemo(() => {
    return {
      textMovement: [0.05, 1],
      textFade: [0.5, 1],
      highlightMovement: [0.1, 0.6, 1],
      highlightFade: [0, 0.2, 0.63, 0.68, 0.98, 1],
      scaleProgression: [0, 0.2, 0.8, 1],
      backgroundFade: [0, 0.1, 0.7, 0.8, 0.9, 1],
      circleGrowth: [0, 0.1, 0.7, 0.8, 0.9, 1, 1.5],
    };
  }, []);

  // Optimized transform values using framer-motion's useTransform directly
  const textPosX = useTransform(scrollYProgress, scrollRanges.textMovement, [
    "20%",
    "-170%",
  ]);
  const textOpacity = useTransform(
    scrollYProgress,
    scrollRanges.textFade,
    [1, 0.5]
  );

  const highlightTextPosX = useTransform(
    scrollYProgress,
    scrollRanges.highlightMovement,
    ["20%", "33%", "33%"]
  );
  const higlightTextOpacity = useTransform(
    scrollYProgress,
    scrollRanges.highlightFade,
    [0, 0, 0, 1, 1, 0]
  );

  const spanScale = useTransform(
    scrollYProgress,
    scrollRanges.scaleProgression,
    [1, 1.5, 1.5, 2]
  );

  const circleSize = useTransform(
    scrollYProgress,
    scrollRanges.circleGrowth,
    [0, 400, 1000, 5000, 6000, 6000, 6000] // Original sizes for desktop
  );
  const position = useTransform(
    scrollYProgress,
    scrollRanges.circleGrowth,
    ["fixed", "fixed", "fixed", "fixed", "fixed", "fixed","absolute"]
  );


  // Create a smooth transition for circle visibility using a single boolean input
  const circleVisibility = "true"

  const backgroundColor = "white";

  // Smooth opacity transition based on section visibility
  const circleOpacity = useTransform(
    scrollYProgress,
    [0.63, 0.68, 0.9999, 1],
    [0, 1, 1, 1]
  );

  return (
    <motion.section
      ref={targetRef}
      className="flex h-[300vh] items-start justify-start"
      aria-label="Texte animé descriptif"
    >
      <motion.div
        ref={sectionRef}
        onUpdate={(updatedProps) => {
          if (updatedProps.width > 1950) {
            setIsBackgroundWhite(true);
          } else if (updatedProps.width < 1900) {
            setIsBackgroundWhite(false);
          }
        }}
        style={{
          opacity: circleOpacity,
          backgroundColor: backgroundColor,
          width: circleSize,
          height: circleSize,
          borderRadius: "50%",
          position: position,
          top: "50%",
          left: "50%",
          x: "-50%",
          y: "-50%",
          willChange: "transform, opacity, position, visibility",
          visibility: circleVisibility,
        }}
        transition={{
          type: "spring",
          damping: 5,
          stiffness: 100,
          restDelta: 0.001,
          duration: ANIMATION_TIMING.durations.verySlow,
          ease: EASING_CURVES.gentle,
          opacity: {
            duration: 0.8,
            ease: EASING_CURVES.gentle,
          }
        }}
      />
      <div className="sticky top-1/2 left-1/2 min-h-[30rem] sm:min-h-[40rem] lg:min-h-[50rem] min-w-[30rem] sm:min-w-[40rem] lg:min-w-[50rem] -translate-x-1/2 -translate-y-1/2 whitespace-nowrap before:absolute inline-block">
        <motion.div
          aria-hidden
          style={{
            x: textPosX,
            y: "-40%",
            fontFamily: "Lexend",
            opacity: textOpacity,
            willChange: "transform, opacity",
          }}
          className="whitepspace-prewrap min-w-screen absolute top-1/2 left-[calc(-50vw+25rem)] z-[11] text-[clamp(8rem,15vw,23rem)] text-white [-webkit-text-stroke:1px var(--color-heading)]"
        >
          <ReactMarkdown
            escapeHtml={false}
            components={{
              strong: ({ node, ...props }) => (
                <span
                  className="text-[#00ffcc] font-bold"
                  style={{ fontSize: "300pt" }}
                >
                  <motion.span
                    style={{
                      scale: spanScale,
                      x: highlightTextPosX,
                      opacity: higlightTextOpacity,
                      willChange: "transform, opacity",
                    }}
                    className="text-white"
                    {...props}
                  />
                </span>
              ),
            }}
          >
            {textes?.textScrolling.split("*")[0]}
          </ReactMarkdown>
        </motion.div>
        <motion.span
          style={{
            position: "fixed",
            top: "60%",
            left: "50%",
            x: "-50%",
            y: "-50%",
            opacity: higlightTextOpacity,
            willChange: "transform, opacity",
          }}
          className="whitepspace-prewrap min-w-screen absolute top-[10vh] left-[calc(-50vw+25rem)] z-[11] text-[clamp(8rem,15vw,23rem)] text-white [-webkit-text-stroke:1px var(--color-heading)]"
        >
          <ReactMarkdown
            escapeHtml={false}
            components={{
              strong: ({ node, ...props }) => (
                <span
                  className="text-[#00ffcc] font-bold"
                  style={{
                    fontSize: "300pt",
                    fontFamily: "SAM",
                    color: "#00FFCC",
                    zIndex: 13,
                    letterSpacing: "-20px",
                  }}
                  {...props}
                />
              ),
            }}
          >
            {textes?.textScrolling.slice(
              textes?.textScrolling.split("*")[0].length
            )}
          </ReactMarkdown>
        </motion.span>
      </div>
    </motion.section>
  );
});

SAMText.displayName = "SAMText";

export default SAMText;
